import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    OnInit,
    computed,
    inject,
    signal,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { elementCancel, elementClock, elementDocument, elementDocuments, elementSearch } from '@siemens/element-icons';
import { SiEmptyStateComponent } from '@siemens/element-ng/empty-state';
import { addIcons, SiIconComponent } from '@siemens/element-ng/icon';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { Subject, debounceTime, filter, tap } from 'rxjs';
import { GlobalSearchStoreService } from '@core/services/global-search-store.service';
import {
    GLOBAL_SEARCH_LISTBOX_ID,
    MIN_SEARCH_TERM_LENGTH,
    SEARCH_DEBOUNCE_TIME_MS
} from '@shared/constants/global-search.constants';
import { SearchResult, SearchResultGroup } from '@shared/models/search-result.model';
import { splitOnTerm } from '@shared/utils/global-search.utils';

@Component({
    selector: 'app-global-search',
    templateUrl: './global-search.component.html',
    imports: [SiEmptyStateComponent, SiIconComponent, SiLoadingSpinnerComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'relative block',
        '(document:click)': 'onDocumentClick($event)',
        '(document:keydown)': 'onDocumentKeydown($event)'
    }
})
export class GlobalSearchComponent implements OnInit {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly searchStore = inject(GlobalSearchStoreService);

    private readonly searchInput = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');
    private readonly typedTerm = new Subject<string>();

    protected readonly term = this.searchStore.term;
    protected readonly isLoading = this.searchStore.isLoading;
    protected readonly hasError = this.searchStore.hasError;
    protected readonly resultCount = this.searchStore.resultCount;
    protected readonly recentTerms = this.searchStore.recentTerms;

    private readonly sequences = this.searchStore.sequences;
    private readonly nuggets = this.searchStore.nuggets;

    protected readonly isOpen = signal(false);
    protected readonly activeIndex = signal(-1);
    protected readonly inputValue = signal('');

    protected readonly minTermLength = MIN_SEARCH_TERM_LENGTH;
    protected readonly listboxId = GLOBAL_SEARCH_LISTBOX_ID;

    protected readonly icons = addIcons({
        elementCancel,
        elementClock,
        elementDocument,
        elementDocuments,
        elementSearch
    });

    protected readonly hasSearchableTerm = computed(() => this.term().length >= MIN_SEARCH_TERM_LENGTH);

    protected readonly groups = computed<SearchResultGroup[]>(() => {
        const term = this.term();
        let index = 0;

        const toGroup = (key: string, label: string, icon: string, results: SearchResult[]): SearchResultGroup => ({
            key,
            label,
            icon,
            results: results.map(result => ({
                ...result,
                index: index++,
                titleParts: splitOnTerm(result.title, term)
            }))
        });

        return [
            toGroup('sequences', 'Themen', this.icons.elementDocuments, this.sequences()),
            toGroup('nuggets', 'Nuggets', this.icons.elementDocument, this.nuggets())
        ].filter(group => group.results.length > 0);
    });

    private readonly flatResults = computed(() => this.groups().flatMap(group => group.results));

    /** True while the result listbox is actually rendered, which drives `aria-expanded`. */
    protected readonly isListboxRendered = computed(
        () =>
            this.isOpen() &&
            !this.isLoading() &&
            !this.hasError() &&
            this.hasSearchableTerm() &&
            this.groups().length > 0
    );

    protected readonly activeDescendant = computed(() =>
        this.isListboxRendered() && this.activeIndex() >= 0 ? this.optionId(this.activeIndex()) : null
    );

    public ngOnInit(): void {
        this.searchWhileTyping();
        this.closeOnNavigation();
    }

    protected optionId(index: number): string {
        return `global-search-result-${index}`;
    }

    protected onInput(event: Event): void {
        const term = (event.target as HTMLInputElement).value;
        this.inputValue.set(term);
        this.typedTerm.next(term);
    }

    protected clearInput(): void {
        this.inputValue.set('');
        this.onTermChange('');
        this.searchInput().nativeElement.focus();
    }

    protected open(): void {
        this.isOpen.set(true);
    }

    protected close(): void {
        this.isOpen.set(false);
        this.activeIndex.set(-1);
    }

    protected applyRecentTerm(term: string): void {
        this.inputValue.set(term);
        this.activeIndex.set(-1);
        this.searchStore.search({ term });
        this.searchInput().nativeElement.focus();
    }

    protected forgetRecentTerms(): void {
        this.searchStore.clearRecentTerms();
    }

    protected selectResult(result: SearchResult): void {
        this.searchStore.rememberTerm({ term: this.term() });
        this.reset();

        void this.router.navigateByUrl(result.routerLink);
    }

    protected onKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.moveActiveIndex(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.moveActiveIndex(-1);
                break;
            case 'Enter': {
                const result = this.flatResults()[this.activeIndex()];
                if (result) {
                    event.preventDefault();
                    this.selectResult(result);
                }
                break;
            }
            case 'Escape':
                event.preventDefault();
                this.close();
                break;
            default:
                break;
        }
    }

    protected onDocumentClick(event: MouseEvent): void {
        if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
            this.close();
        }
    }

    protected onDocumentKeydown(event: KeyboardEvent): void {
        if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            this.open();
            this.searchInput().nativeElement.focus();
        } else if (event.key === 'Escape' && this.isOpen()) {
            this.close();
        }
    }

    private moveActiveIndex(offset: number): void {
        const results = this.flatResults();
        if (results.length === 0) {
            return;
        }

        this.isOpen.set(true);
        const current = this.activeIndex();
        const next =
            current === -1
                ? offset > 0
                    ? 0
                    : results.length - 1
                : (current + offset + results.length) % results.length;

        this.activeIndex.set(next);
        document.getElementById(this.optionId(next))?.scrollIntoView({ block: 'nearest' });
    }

    private searchWhileTyping(): void {
        this.typedTerm
            .pipe(
                debounceTime(SEARCH_DEBOUNCE_TIME_MS),
                // Drops stale keystrokes once the input was cleared or replaced programmatically.
                filter(term => term === this.inputValue()),
                tap(term => this.onTermChange(term)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private closeOnNavigation(): void {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                tap(() => this.close()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private onTermChange(term: string): void {
        this.activeIndex.set(-1);
        this.isOpen.set(true);
        this.searchStore.search({ term });
    }

    private reset(): void {
        this.inputValue.set('');
        this.searchStore.clear();
        this.close();
    }
}
